// frontend/src/pages/VerificationPage.js
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

const VerificationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const status = searchParams.get('status');
  
  const [countdown, setCountdown] = useState(5);
  const [showCountdown, setShowCountdown] = useState(false);

  useEffect(() => {
    // Start countdown for successful verification
    if (status === 'success' || status === 'already-verified') {
      setShowCountdown(true);
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            navigate('/login');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [status, navigate]);

  const getVerificationContent = () => {
    switch (status) {
      case 'success':
        return {
          icon: '✅',
          title: 'Email Verified Successfully!',
          message: 'Your email has been verified successfully. You can now log in to your Agent Digest account.',
          buttonText: 'Go to Login',
          buttonAction: () => navigate('/login'),
          buttonClass: 'bg-green-600 hover:bg-green-700 focus:ring-green-500',
          cardClass: 'border-green-200 bg-green-50'
        };
      
      case 'already-verified':
        return {
          icon: '✓',
          title: 'Email Already Verified',
          message: 'Your email was already verified. You can log in to your account.',
          buttonText: 'Go to Login',
          buttonAction: () => navigate('/login'),
          buttonClass: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
          cardClass: 'border-blue-200 bg-blue-50'
        };
      
      case 'invalid':
        return {
          icon: '❌',
          title: 'Invalid Verification Link',
          message: 'This verification link is invalid or has expired. Please request a new verification email from the registration page.',
          buttonText: 'Go to Registration',
          buttonAction: () => navigate('/register'),
          buttonClass: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
          cardClass: 'border-red-200 bg-red-50'
        };
      
      case 'error':
        return {
          icon: '⚠️',
          title: 'Verification Error',
          message: 'There was an error verifying your email. Please try again or contact our support team for assistance.',
          buttonText: 'Try Again',
          buttonAction: () => navigate('/register'),
          buttonClass: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
          cardClass: 'border-red-200 bg-red-50'
        };
      
      default:
        return {
          icon: '📧',
          title: 'Email Verification',
          message: 'Processing your email verification...',
          buttonText: 'Go to Home',
          buttonAction: () => navigate('/'),
          buttonClass: 'bg-gray-600 hover:bg-gray-700 focus:ring-gray-500',
          cardClass: 'border-gray-200 bg-gray-50'
        };
    }
  };

  const content = getVerificationContent();
  const isSuccess = status === 'success' || status === 'already-verified';

  return (
    <div className="min-h-[calc(100vh-var(--navbar-height,88px)-var(--footer-height,88px))] flex flex-col items-center justify-center bg-gradient-to-b from-white to-blue-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className={`w-full max-w-md space-y-8 text-center bg-white p-8 sm:p-10 shadow-xl rounded-lg border-2 ${content.cardClass}`}>
        {/* Header */}
        <Link to="/" className="inline-block mb-6">
          <h1 className="text-3xl sm:text-4xl font-bold text-blue-600 hover:text-blue-700 transition-colors">
            Agent Digest
          </h1>
        </Link>

        {/* Icon */}
        <div className="text-6xl mb-4">
          {content.icon}
        </div>

        {/* Title */}
        <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-gray-900">
          {content.title}
        </h2>

        {/* Message */}
        <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
          {content.message}
        </p>

        {/* Countdown for successful verification */}
        {showCountdown && isSuccess && (
          <div className="bg-blue-100 border border-blue-200 rounded-md p-3">
            <p className="text-blue-800 text-sm">
              Redirecting to login in {countdown} second{countdown !== 1 ? 's' : ''}...
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="mt-8 space-y-4">
          <button
            onClick={content.buttonAction}
            className={`group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white ${content.buttonClass} focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors`}
          >
            {content.buttonText}
          </button>
          
          {/* Additional actions based on status */}
          {(status === 'invalid' || status === 'error') && (
            <div className="space-y-2">
              <button
                onClick={() => navigate('/login')}
                className="w-full py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                Already have an account? Login
              </button>
            </div>
          )}
          
          {/* Back to Home link */}
          <div className="text-center pt-4">
            <Link
              to="/"
              className="text-blue-600 hover:text-blue-500 text-sm font-medium transition-colors"
            >
              ← Back to Home
            </Link>
          </div>
        </div>

        {/* Help text */}
        {(status === 'invalid' || status === 'error') && (
          <div className="mt-6 p-4 bg-gray-50 rounded-md">
            <p className="text-xs text-gray-500">
              Need help? Contact our support team at{' '}
              <a 
                href="mailto:support@agentdigest.io" 
                className="text-blue-600 hover:text-blue-500 font-medium"
              >
                support@agentdigest.io
              </a>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerificationPage;
